import createDebug from 'debug';
import { RetryPromiseWithBackoff } from './RetryPromiseWithBackoff';
import { DurableRecording } from '@/constants';

const debug = createDebug('durable:RawChunkUploadPreSignedUrls');

/**
 * @param {...*} args
 */
const logError = (...args) => {
  console.error('RawChunkUploadPreSignedUrls:', ...args);
};

export class RawChunkUploadPreSignedUrls {
  /** @typedef {{ rawChunkNumber: number; rawChunkUploadPreSignedUrl: string}} PreSignedUrl */

  /** @type {Map<number, PreSignedUrl>} */
  urls = new Map();

  constructor(videoId, containerFormat, recordingId, recordingETag, vesApiHelper) {
    this.videoId = videoId;
    this.containerFormat = containerFormat;
    this.recordingId = recordingId;
    this.recordingETag = recordingETag;
    this.vesApiHelper = vesApiHelper;
    this.fetchUrlBatchPromise = Promise.resolve();
  }

  /**
   * @param {PreSignedUrl[]} newUrls
   */
  addUrls(newUrls) {
    for (const newUrl of newUrls) {
      this.urls.set(newUrl.rawChunkNumber, newUrl);
    }
  }

  /**
   * @param {number} rawChunkNumber
   * @returns {Promise<string>}
   */
  async getUrlForRawChunkNumber(rawChunkNumber) {
    // If we already have the chunk URL, return it immediately
    if (this.urls.has(rawChunkNumber)) {
      return this.getPreSignedUrlForRawChunkNumber(rawChunkNumber);
    }

    // Otherwise let's attempt to get the URL after fetching the next batch of URLs
    await this.fetchUrlBatchPromise;

    /**
     * If we don't have the chunk URL yet after fetching the next batch, we need to fetch new URL
     * batches until we have it. And we don't want to retry with backoff since we want to get the
     * next batch of URLs as soon as possible.
     */
    const maxRetries = 50;
    let currentRetry = 1;
    while (currentRetry < maxRetries && !this.urls.has(rawChunkNumber)) {
      await this.fetchUrlBatch();
      currentRetry++;
    }

    return this.getPreSignedUrlForRawChunkNumber(rawChunkNumber);
  }

  /**
   * @param {number} rawChunkNumber
   * @returns {string}
   */
  getPreSignedUrlForRawChunkNumber(rawChunkNumber) {
    const chunkData = this.urls.get(rawChunkNumber);

    if (!chunkData || !chunkData.rawChunkUploadPreSignedUrl) {
      throw new Error('Could not find a pre-signed URL for the raw chunk to upload');
    }

    return chunkData.rawChunkUploadPreSignedUrl;
  }

  getMaxRawChunkNumber() {
    const methodDebug = debug.extend('getMaxRawChunkNumber');

    if (this.urls.size === 0) {
      methodDebug('no urls');
      return 0;
    }

    const maxRawChunkNumber = Math.max(...this.urls.keys());
    methodDebug('maxRawChunkNumber', maxRawChunkNumber);
    return maxRawChunkNumber;
  }

  async fetchUrlBatch() {
    this.fetchUrlBatchPromise = this.getFetchUrlBatchPromise();
    await this.fetchUrlBatchPromise;
  }

  async getFetchUrlBatchPromise() {
    try {
      const { videoId, containerFormat, recordingId, recordingETag, vesApiHelper } = this;

      const maxRawChunkNumber = this.getMaxRawChunkNumber();
      const nextStartingRawChunkNumber = maxRawChunkNumber + 1;
      const nextEndingRawChunkNumber =
        maxRawChunkNumber + DurableRecording.PRE_SIGNED_URL_BATCH_SIZE;
      const retryWithBackoff = new RetryPromiseWithBackoff();
      const rawChunkUploadPreSignedUrls = await retryWithBackoff.run(async () => {
        const urlResponse = await vesApiHelper.createRawChunkUploadPreSignedUrls(
          videoId,
          containerFormat,
          recordingId,
          recordingETag,
          nextStartingRawChunkNumber,
          nextEndingRawChunkNumber,
        );

        if (!urlResponse || !urlResponse.rawChunkUploadPreSignedUrls) {
          throw new Error('No pre-signed chunk upload urls returned');
        }

        return urlResponse.rawChunkUploadPreSignedUrls;
      });

      this.addUrls(rawChunkUploadPreSignedUrls);
    } catch (error) {
      logError('Error setting pre-signed chunk upload url', error);
      throw error;
    }
  }
}
